/**
 * ElementInspector
 *
 * Right pane:
 *   - shows the selected violation's code citation + rationale
 *   - looks up the underlying element's BIM properties from the
 *     pre-extracted properties.json (see propertiesStore + Phase 1 ADR)
 *   - Phase 3 will add the cited code passage via @vera/retrieval
 */
import { isUniversalSheet } from "../data/sheets";
import { useEffectiveSelectedViolation } from "../hooks/useEffectiveSelectedViolation";
import { useEngineStore } from "../stores/engineStore";
import { usePropertiesStore } from "../stores/propertiesStore";
import { useViewerStore } from "../stores/viewerStore";
import { CodeSourcePanel } from "./CodeSourcePanel";
import { SheetPicker } from "./SheetPicker";
import { UiModeToggle } from "./UiModeToggle";

/**
 * Pretty-print numeric strings stored verbatim from SQLite (often long
 * floats like "26.97119999999995"). Non-numeric strings pass through.
 */
function prettyValue(v: string): string {
  if (v === "") return "—";
  const n = Number(v);
  if (!Number.isNaN(n) && Number.isFinite(n) && /^-?\d/.test(v)) {
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(3).replace(/\.?0+$/, "");
  }
  return v;
}

export function ElementInspector() {
  const selected = useEffectiveSelectedViolation();
  const currentSheetId = useViewerStore(s => s.currentSheetId);

  const propsLoaded = usePropertiesStore(s => s.loaded);
  const propsError = usePropertiesStore(s => s.loadError);
  const lookup = usePropertiesStore(s => s.get);
  const rules = useEngineStore(s => s.rules);

  const ruleSpec = selected?.provenance?.ruleId
    ? rules.find(r => r.ruleId === selected.provenance?.ruleId)
    : undefined;

  // The 3D view contains every model element, so it always counts as a
  // matching context for any violation — no need to switch sheets to see
  // properties.
  const sheetMatches =
    !selected?.preferredSheetId ||
    selected.preferredSheetId === currentSheetId ||
    isUniversalSheet(currentSheetId);

  const element =
    selected && typeof selected.elementDbId === "number"
      ? lookup(selected.elementDbId)
      : undefined;

  return (
    <aside
      style={{
        borderLeft: "1px solid var(--vera-border)",
        background: "var(--vera-panel-bg)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          padding: "12px 16px",
          borderBottom: "1px solid var(--vera-border)",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <UiModeToggle />
        <SheetPicker />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16 }}>
        <div
          style={{
            fontSize: 12,
            textTransform: "uppercase",
            color: "var(--vera-text-dim)",
            letterSpacing: 0.5,
            marginBottom: 12,
          }}
        >
          Element Inspector
        </div>

        {!selected && (
          <div style={{ color: "var(--vera-text-dim)", fontSize: 13 }}>
            Select a violation on the left to see element details and code
            source.
          </div>
        )}

        {selected && (
          <>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {selected.code}
            </div>
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              {selected.summary}
            </div>
            {selected.rationale && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--vera-text-dim)",
                  marginBottom: 16,
                  lineHeight: 1.45,
                }}
              >
                {selected.rationale}
              </div>
            )}

            {(selected.provenance?.userSupplied?.facts.length ?? 0) > 0 && (
              <>
                <div
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    color: "#e87070",
                    letterSpacing: 0.5,
                    borderTop: "1px solid var(--vera-border)",
                    paddingTop: 12,
                    marginTop: 4,
                    marginBottom: 8,
                  }}
                >
                  Verified by you
                </div>
                <ul
                  style={{
                    margin: "0 0 16px",
                    paddingLeft: 18,
                    fontSize: 12,
                    lineHeight: 1.5,
                  }}
                >
                  {selected.provenance?.userSupplied?.facts.map(f => (
                    <li key={f.factKey} style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 500 }}>
                        {f.label ?? f.factKey}
                      </span>
                      {": "}
                      {typeof f.value === "object" &&
                      f.value &&
                      "value" in f.value
                        ? `${f.value.value} ${f.value.unit}`
                        : String(f.value)}
                      <div
                        style={{ color: "var(--vera-text-dim)", marginTop: 2 }}
                      >
                        v{f.version} · first supplied{" "}
                        {new Date(f.suppliedAt).toLocaleString()} · updated{" "}
                        {new Date(f.updatedAt).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <CodeSourcePanel violation={selected} rule={ruleSpec} />

            {ruleSpec?.ruleDetails && (
              <>
                <div
                  style={{
                    fontSize: 11,
                    textTransform: "uppercase",
                    color: "var(--vera-text-dim)",
                    letterSpacing: 0.5,
                    borderTop: "1px solid var(--vera-border)",
                    paddingTop: 12,
                    marginTop: 16,
                    marginBottom: 8,
                  }}
                >
                  Rule details
                </div>
                {ruleSpec.ruleDetails.summary && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--vera-text-dim)",
                      marginBottom: 10,
                      lineHeight: 1.45,
                    }}
                  >
                    {ruleSpec.ruleDetails.summary}
                  </div>
                )}
                <ol
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    fontSize: 12,
                    lineHeight: 1.5,
                  }}
                >
                  {ruleSpec.ruleDetails.steps.map(step => (
                    <li key={step.label} style={{ marginBottom: 8 }}>
                      <span style={{ fontWeight: 500 }}>{step.label}</span>
                      {step.detail && (
                        <div
                          style={{
                            color: "var(--vera-text-dim)",
                            marginTop: 2,
                          }}
                        >
                          {step.detail}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              </>
            )}

            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                color: "var(--vera-text-dim)",
                letterSpacing: 0.5,
                borderTop: "1px solid var(--vera-border)",
                paddingTop: 12,
                marginBottom: 8,
              }}
            >
              Element properties (dbId {selected.elementDbId ?? "—"})
            </div>

            {!sheetMatches && (
              <div style={{ fontSize: 12, color: "var(--vera-text-dim)" }}>
                Element lives on a different sheet
                {selected.preferredSheetId
                  ? ` (${selected.preferredSheetId})`
                  : ""}
                . Switch sheets or click the violation again to view it.
              </div>
            )}

            {sheetMatches && !propsLoaded && (
              <div style={{ fontSize: 12, color: "var(--vera-text-dim)" }}>
                Loading properties…
              </div>
            )}

            {sheetMatches && propsError && (
              <div style={{ fontSize: 12, color: "var(--vera-violation)" }}>
                Failed to load properties: {propsError}
              </div>
            )}

            {sheetMatches && propsLoaded && !propsError && !element && (
              <div style={{ fontSize: 12, color: "var(--vera-text-dim)" }}>
                No properties found for dbId {selected.elementDbId}.
              </div>
            )}

            {sheetMatches && element && (
              <>
                {(element.name || element.category) && (
                  <div
                    style={{
                      fontSize: 13,
                      marginBottom: 8,
                    }}
                  >
                    {element.name && (
                      <div>
                        <strong>{element.name}</strong>
                      </div>
                    )}
                    {element.category && (
                      <div
                        style={{ color: "var(--vera-text-dim)", fontSize: 12 }}
                      >
                        {element.category}
                      </div>
                    )}
                  </div>
                )}
                <table
                  style={{
                    width: "100%",
                    fontSize: 12,
                    borderCollapse: "collapse",
                  }}
                >
                  <tbody>
                    {element.properties.map((p, i) => (
                      <tr
                        key={`${p.displayCategory}-${p.displayName}-${i}`}
                        style={{ borderBottom: "1px solid var(--vera-border)" }}
                      >
                        <td
                          style={{
                            padding: "4px 6px 4px 0",
                            color: "var(--vera-text-dim)",
                            verticalAlign: "top",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {p.displayName}
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--vera-text-dim)",
                              opacity: 0.7,
                            }}
                          >
                            {p.displayCategory}
                          </div>
                        </td>
                        <td
                          style={{
                            padding: "4px 0 4px 6px",
                            wordBreak: "break-word",
                          }}
                        >
                          {prettyValue(p.displayValue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
